import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// Create axios instance
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url;
      
      // Don't auto-logout on login failures - let the login component handle it
      if (url?.includes('/auth/login')) {
        return Promise.reject(error);
      }
      
      // For other 401 errors (expired tokens, etc.), auto-logout
      console.log('Authentication token expired, redirecting to login');
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API functions
export const authAPI = {
  login: (credentials: { username: string; password: string }) =>
    api.post('/auth/login', credentials),
  
  register: (userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    inviteToken: string;
    privacyAgreementAccepted?: boolean;
    termsOfServiceAccepted?: boolean;
  }) => api.post('/auth/register', userData),

  verifyEmail: (token: string) =>
    api.post('/auth/verify-email', { token }),
  
  getCurrentUser: () => api.get('/auth/me'),
  
  updateProfile: (data: { firstName: string; lastName: string }) =>
    api.put('/auth/profile', data),
  
  changePassword: (passwords: { currentPassword: string; newPassword: string }) =>
    api.put('/auth/change-password', passwords),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (data: { token: string; newPassword: string }) =>
    api.post('/auth/reset-password', data),
};

export const invitesAPI = {
  list: () => api.get('/invites'),
  create: (data: {
    roles?: ('TRAINER' | 'CLIENT')[];
    maxUses?: number;
    expiresInDays?: number;
    note?: string;
  }) => api.post('/invites', data),
  revoke: (id: string) => api.delete(`/invites/${id}`),
};

export interface StripeConnectStatus {
  connected: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  readyToReceivePayments: boolean;
  currentlyDue: string[];
}

export const paymentsAPI = {
  getConnectStatus: () =>
    api.get<StripeConnectStatus>('/payments/connect/status'),

  createConnectAccountLink: () =>
    api.post<{ url: string }>('/payments/connect/account-link'),

  setClientRate: (clientId: string, monthlyRateCents: number) =>
    api.put<{ clientId: string; monthlyRateCents: number }>(
      `/payments/clients/${clientId}/rate`,
      { monthlyRateCents }
    ),

  getSubscription: (clientId?: string) =>
    api.get<{
      subscription: SubscriptionInfo | null;
      monthlyRateCents: number | null;
      trainerId: string | null;
    }>('/payments/subscription', { params: clientId ? { clientId } : undefined }),

  createCheckoutSession: () =>
    api.post<{ url: string }>('/payments/checkout-session'),

  syncSubscription: () =>
    api.post<{
      subscription: SubscriptionInfo | null;
      monthlyRateCents: number | null;
      trainerId: string | null;
    }>('/payments/subscription/sync'),

  pauseSubscription: (clientId?: string) =>
    api.post<{ subscription: SubscriptionInfo }>('/payments/subscription/pause', clientId ? { clientId } : {}),

  resumeSubscription: (clientId?: string) =>
    api.post<{ subscription: SubscriptionInfo }>('/payments/subscription/resume', clientId ? { clientId } : {}),

  cancelSubscription: (clientId?: string) =>
    api.post<{ subscription: SubscriptionInfo }>('/payments/subscription/cancel', clientId ? { clientId } : {}),
};

export interface SubscriptionInfo {
  id: string;
  clientId: string;
  trainerId: string;
  monthlyAmountCents: number;
  currency: string;
  applicationFeePercent: number;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd?: string;
  pausedAt?: string;
  canceledAt?: string;
  canceledByRole?: 'TRAINER' | 'CLIENT' | 'SYSTEM' | null;
  canceledByUserId?: string | null;
}

// Onboarding API
export const onboardingAPI = {
  getStatus: () => api.get('/onboarding/status'),
  submitWaiver: (data: { name: string }) => api.post('/onboarding/waiver', data),
  submitPARQ: (data: any) => api.post('/onboarding/parq', data),
  submitGoalsActivities: (data: any) => api.post('/onboarding/goals-activities', data),
  submitMedicalHistory: (data: any) => api.post('/onboarding/medical-history', data),
  submitNutrition: (data: any) => api.post('/onboarding/nutrition', data),
  submitLifestyle: (data: any) => api.post('/onboarding/lifestyle', data),
};

export const clientsAPI = {
  getAll: () => api.get('/clients'),
  
  getById: (id: string) => api.get(`/clients/${id}`),
  
  create: (clientData: {
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    password: string;
    birthDate?: string;
    gender?: string;
    phoneNumber?: string;
    address?: string;
    emergencyContact?: string;
    emergencyPhone?: string;
    membershipType?: string;
    membershipRate?: number;
    invoiceDate?: string;
  }) => api.post('/clients', clientData),
  
  update: (id: string, clientData: {
    firstName: string;
    lastName: string;
    email: string;
    birthDate?: string;
    gender?: string;
    phoneNumber?: string;
    address?: string;
    emergencyContact?: string;
    emergencyPhone?: string;
    membershipType?: string;
    membershipRate?: number;
    invoiceDate?: string;
  }) => api.put(`/clients/${id}`, clientData),
  
  delete: (id: string) => api.delete(`/clients/${id}`),
  
  getSummary: (id: string) => api.get(`/clients/${id}/summary`),
};

export const workoutsAPI = {
  getAll: (userId?: string) => 
    api.get('/workouts', { params: { userId } }),
  
  getById: (id: string) => api.get(`/workouts/${id}`),
  
  create: (workoutData: {
    name: string;
    description?: string;
    scheduledDate?: string;
    estimatedDuration?: number;
    assignedTo: string;
    sets: any[];
    tags?: string[];
  }) => api.post('/workouts/create', workoutData),
  
  update: (id: string, workoutData: {
    name: string;
    description?: string;
    scheduledDate?: string;
    estimatedDuration?: number;
    assignedTo: string;
    sets: any[];
    tags?: string[];
  }) => api.put(`/workouts/${id}`, workoutData),
  
  delete: (id: string) => api.delete(`/workouts/${id}`),
  
  complete: (id: string) => api.post(`/workouts/${id}/complete`),
};

export const exercisesAPI = {
  getAll: () => api.get('/exercises'),
  
  getById: (id: string) => api.get(`/exercises/${id}`),
  
  create: (exerciseData: {
    name: string;
    description?: string;
    category?: string;
    muscleGroup?: string;
    difficulty?: string;
    videoUrl?: string;
    imageUrl?: string;
  }) => api.post('/exercises', exerciseData),
  
  update: (id: string, exerciseData: {
    name: string;
    description?: string;
    category?: string;
    muscleGroup?: string;
    difficulty?: string;
    videoUrl?: string;
    imageUrl?: string;
  }) => api.put(`/exercises/${id}`, exerciseData),
  
  delete: (id: string) => api.delete(`/exercises/${id}`),
};

export const equipmentAPI = {
  getAll: () => api.get('/equipment'),
  
  getById: (id: string) => api.get(`/equipment/${id}`),
  
  create: (equipmentData: {
    name: string;
    description?: string;
    category?: string;
    imageUrl?: string;
  }) => api.post('/equipment', equipmentData),
  
  update: (id: string, equipmentData: {
    name: string;
    description?: string;
    category?: string;
    imageUrl?: string;
  }) => api.put(`/equipment/${id}`, equipmentData),
  
  delete: (id: string) => api.delete(`/equipment/${id}`),
};

export const mealsAPI = {
  getAll: () =>
    api.get('/meals'),

  getById: (id: string) => api.get(`/meals/${id}`),

  getForClient: (clientId: string) => api.get(`/meals/client/${clientId}`),

  create: (mealPlanData: {
    name: string;
    description?: string;
    assignedTo: string;
    startDate: string;
    endDate: string;
    meals: any[];
    tags?: string[];
  }) => api.post('/meals/create', mealPlanData),

  update: (id: string, mealPlanData: {
    name?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    meals?: any[];
    tags?: string[];
  }) => api.put(`/meals/${id}`, mealPlanData),

  delete: (id: string) => api.delete(`/meals/${id}`),

  // Client: toggle a food item completed
  markFoodItem: (id: string, data: {
    mealIndex: number;
    foodIndex: number;
    completed: boolean;
  }) => api.patch(`/meals/${id}`, data),

  // Client: complete whole plan + submit feedback
  complete: (id: string, data: {
    overallFeedback?: string;
    overallRating?: number;
    completedDate?: string;
  }) => api.patch(`/meals/${id}/complete`, data),
};

export const progressAPI = {
  getAll: (userId?: string) => 
    api.get('/progress', { params: { userId } }),
  
  getById: (id: string) => api.get(`/progress/${id}`),
  
  create: (progressData: {
    date: string;
    weight?: number;
    bodyFat?: number;
    muscleMass?: number;
    notes?: string;
    images?: string[];
    userId: string;
  }) => api.post('/progress', progressData),
  
  update: (id: string, progressData: {
    date: string;
    weight?: number;
    bodyFat?: number;
    muscleMass?: number;
    notes?: string;
    images?: string[];
  }) => api.put(`/progress/${id}`, progressData),
  
  delete: (id: string) => api.delete(`/progress/${id}`),
};

export const setTypesAPI = {
  getAll: () => api.get('/set-types'),
};
